/**
 * Request Queue Composable
 *
 * Manages a queue of async requests with concurrency control and cancellation support.
 * Useful for preventing browser request throttling when making many API calls.
 *
 * @param {Object} options Configuration options
 * @param {number} options.concurrency Maximum number of concurrent requests (default: 6)
 * @returns {Object} Queue management functions
 */
export function useRequestQueue ({ concurrency = 6 } = {}) {
  let queue = []
  let activeRequests = 0
  let abortController = new AbortController()
  let isCancelled = false

  /**
   * Process the next request in the queue
   */
  const processNext = async () => {
    if (isCancelled || queue.length === 0 || activeRequests >= concurrency) {
      return
    }

    const { fn, resolve, reject } = queue.shift()
    activeRequests++

    try {
      // Pass abort signal to the request function
      const result = await fn(abortController.signal)
      if (!isCancelled) {
        resolve(result)
      }
    } catch (error) {
      if (!isCancelled && error.name !== 'AbortError') {
        reject(error)
      }
    } finally {
      activeRequests--
      // Process next item in queue
      processNext()
    }
  }

  /**
   * Add a request to the queue
   * @param {Function} fn Async function that performs the request, receives AbortSignal as parameter
   * @returns {Promise} Resolves when the request completes
   */
  const enqueue = (fn) => {
    return new Promise((resolve, reject) => {
      if (isCancelled) {
        reject(new Error('Queue has been cancelled'))
        return
      }

      queue.push({ fn, resolve, reject })
      processNext()
    })
  }

  /**
   * Add multiple requests to the queue
   * @param {Array<Function>} functions Array of async functions
   * @returns {Promise} Resolves when all requests complete
   */
  const enqueueAll = async (functions) => {
    const promises = functions.map(fn => enqueue(fn))
    return Promise.all(promises)
  }

  /**
   * Cancel all pending and in-flight requests
   */
  const cancel = () => {
    isCancelled = true
    abortController.abort()
    queue = []
    activeRequests = 0
  }

  /**
   * Reset the queue to accept new requests
   */
  const reset = () => {
    cancel()
    isCancelled = false
    abortController = new AbortController()
  }

  return {
    enqueue,
    enqueueAll,
    cancel,
    reset
  }
}